import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    IconButton,
    Typography,
    styled,
    ButtonProps
} from '@mui/material';
import {
    Close as CloseIcon,
    CloudUpload as UploadIcon,
    Image as ImageIcon
} from '@mui/icons-material';
import { ActiveModule, EditModalProps } from './utils';
import { uploadDocument } from "../../services/bankingService";
import { openPopup } from "../../stateManagement/actions/popupActions";

const UploadButton = styled(Button)<ButtonProps>(() => ({
    backgroundColor: '#237C9D',
    color: 'white',
    textTransform: 'none',
    padding: '6px',
    width: '275px',
    borderRadius: '8px',
    '&:hover': {
        backgroundColor: '#3A7A94',
    },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: '16px',
        padding: '8px',
        minWidth: '400px',
        maxWidth: '450px',
    },
    '& .MuiDialogActions-root': {
        padding: '16px 20px',
        background: 'white',
        borderTop: '0px solid #e2e8f0',
        margin: 0,
    },
}));

const EditModal: React.FC<EditModalProps> = ({
    open,
    module,
    onClose,
    onSave
}) => {
    const ACCEPTED_FILE_TYPES = "image/png, image/jpeg";
    const MAX_FILE_SIZE = 50 * 1024;
    const [formData, setFormData] = useState({
        label: '',
        subHeading: '',
        icon: '',
        primaryColor: ''
    });
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);
    const colorRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (module) {
            setFormData({
                label: module.label,
                subHeading: module.subHeading,
                icon: module.icon,
                primaryColor: module.primaryColor || '#000000'
            });
            setPreviewUrl(module.icon);
            setSelectedFile(null);
        }
    }, [module]);

    const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const isValidHexColor = (colorString: string) => {
        if (colorString.length < 4) {
            return false;
        }
        return /^#([0-9A-F]{3}){1,2}$/i.test(colorString);
    };

    const validateFile = (file: File): boolean => {
        if (file.size > MAX_FILE_SIZE) {
            openPopup('Alert', 'File size should not exceed 50KB');
            return false;
        }
        return true;
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!validateFile(file)) {
                event.target.value = '';
                return;
            }

            setSelectedFile(file);
            const tempPreviewUrl = URL.createObjectURL(file);
            setPreviewUrl(tempPreviewUrl);
            setFormData(prev => ({
                ...prev,
                icon: ''
            }));
        }
    };

    const uploadFile = async (file: File): Promise<string> => {
        const uploadFormData = new FormData();
        uploadFormData.append("files", file, file.name);

        const fileUploadResponse = await uploadDocument(uploadFormData);
        const blobKey: string = JSON.parse(fileUploadResponse[0].body).features[0].Uri;

        return blobKey;
    };

    const handleSave = async () => {
        if (!module) return;
        if (formData.primaryColor && !isValidHexColor(formData.primaryColor)) {
            openPopup('Alert', 'Please enter a valid hex color value.');
            return;
        }
        setIsSaving(true);
        try {
            let finalIconUrl = formData.icon;

            if (selectedFile) {
                try {
                    finalIconUrl = await uploadFile(selectedFile);
                } catch (error) {
                    console.error('Error uploading file:', error);
                    openPopup('Alert', 'Failed to upload image. Please try again.');
                    return;
                }
            }

            const updatedModule: ActiveModule = {
                ...module,
                label: formData.label.trim(),
                subHeading: formData.subHeading.trim(),
                icon: finalIconUrl,
                primaryColor: formData.primaryColor
            };
            onSave(updatedModule);
            openPopup('Success', 'Module information saved. Click final Save button to confirm all changes.');
            onClose();

        } catch (error) {
            console.error('Error saving module:', error);
            openPopup('Alert', 'Failed to save module information. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        if (selectedFile && previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrl);
        }
        setFormData({
            label: '',
            subHeading: '',
            icon: '',
            primaryColor: ''
        });
        setSelectedFile(null);
        setPreviewUrl('');
        onClose();
    };

    useEffect(() => {
        return () => {
            if (previewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    return (
        <StyledDialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1,
                    fontSize: '17px',
                    fontWeight: 600,
                    color: '#333',
                    background: 'white',
                    borderBottom: '0px'
                }}
            >
                Edit Module
                <IconButton
                    onClick={onClose}
                    sx={{
                        color: '#666',
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pb: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {/* Module label */}
                    <Box>
                        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#333' }}>
                            Module Name
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            value={formData.label}
                            onChange={handleInputChange('label')}
                            disabled={isSaving}
                            variant="outlined"
                            inputProps={{ maxLength: 50 }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#02C7AF',
                                    }
                                }
                            }}
                        />
                    </Box>

                    {/* subHeading */}
                    <Box>
                        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#333' }}>
                            Description
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            size="small"
                            value={formData.subHeading}
                            onChange={handleInputChange('subHeading')}
                            disabled={isSaving}
                            variant="outlined"
                            inputProps={{ maxLength: 50 }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#02C7AF',
                                    }
                                }
                            }}
                        />
                    </Box>

                    {/* Icon */}
                    <Box>
                        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#333' }}>
                            Icon
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box
                                sx={{
                                    width: 50,
                                    height: 50,
                                    border: '1.5px dashed #ddd',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {previewUrl ? (
                                    <img
                                        src={previewUrl}
                                        alt="Module icon"
                                        style={{
                                            width: '42px',
                                            height: '42px',
                                            objectFit: 'contain',
                                            borderRadius: '4px'
                                        }}
                                    />
                                ) : (
                                    <ImageIcon sx={{ color: '#ccc', fontSize: 30 }} />
                                )}
                            </Box>

                            <input
                                accept={ACCEPTED_FILE_TYPES}
                                style={{ display: 'none' }}
                                id="icon-upload"
                                type="file"
                                onChange={handleFileUpload}
                                disabled={isSaving}
                            />
                            <label htmlFor="icon-upload">
                                <UploadButton
                                    component="span"
                                    startIcon={<UploadIcon />}
                                    disabled={isSaving}
                                >
                                    Upload Image
                                </UploadButton>
                            </label>
                        </Box>
                    </Box>

                    {/* Color Picker Section */}
                    <Box>
                        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#333' }}>
                            Color
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <input
                                type="color"
                                ref={colorRef}
                                value={formData.primaryColor || '#000000'}
                                onChange={(e) =>
                                    setFormData(prev => ({ ...prev, primaryColor: e.target.value }))
                                }
                                style={{
                                    width: '60px',
                                    height: '46px',
                                    padding: 0,
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    background: 'none',
                                }}
                            />

                            {/* Hex Input */}
                            <TextField
                                size="small"
                                fullWidth
                                value={formData.primaryColor}
                                onChange={(e) =>
                                    setFormData(prev => ({ ...prev, primaryColor: e.target.value }))
                                }
                                placeholder="#ffffff"
                                disabled={isSaving}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        '&.Mui-focused fieldset': { borderColor: '#02C7AF' },
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ gap: 0.5 }}>
                <Button
                    onClick={handleCancel}
                    disabled={isSaving}
                    variant="outlined"
                    sx={{
                        textTransform: 'none',
                        borderRadius: '6px',
                        borderColor: '#d1d5db',
                        color: '#000000',
                        px: 1.5,
                        py: 0.5,
                        fontSize: '14px',
                        fontWeight: 500,
                        minWidth: '80px',
                        '&:hover': {
                            borderColor: '#9ca3af',
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            color: '#000000'
                        }
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={isSaving || !formData.label.trim()}
                    sx={{
                        textTransform: 'none',
                        backgroundColor: '#3BCDB7',
                        boxShadow: 'none',
                        borderRadius: '6px',
                        px: 1.5,
                        py: 0.5,
                        fontSize: '14px',
                        fontWeight: 600,
                        minWidth: '100px',
                        '&:hover': {
                            backgroundColor: '#57d6c3',
                            boxShadow: 'none'
                        }
                    }}
                >
                    Save Changes
                </Button>
            </DialogActions>
        </StyledDialog>
    );
};

export default EditModal;